import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { guardarDestino } from '@/lib/destino';
import FolhaTalao from './components/FolhaTalao';
import type { RifaImpressao, TalaoImpressao } from './types';
import './imprimir.css';

export default function RifaImprimirPage() {
  const { rifaId } = useParams<{ rifaId: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const idNum = Number(rifaId);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [rifa, setRifa] = useState<RifaImpressao | null>(null);
  const [taloes, setTaloes] = useState<TalaoImpressao[]>([]);
  const [soComLivres, setSoComLivres] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        if (!pronto) return;
        if (!user) {
          guardarDestino();
          navigate(`/entrar?voltar=${encodeURIComponent(`/sorteio/${rifaId}/imprimir`)}`, {
            replace: true,
          });
          return;
        }

        // A RPC faz a checagem de acesso: "Você não administra esta rifa."
        const { data: resTaloes, error: errTaloes } = await supabase.rpc('taloes_para_imprimir', {
          p_rifa: idNum,
        });
        if (errTaloes) {
          if (ativo) setErro(mensagemAmigavel(errTaloes));
          return;
        }

        const { data: resRifa, error: errRifa } = await supabase
          .from('vw_rifa_publica')
          .select('titulo, valor_cota, sorteio_em, sorteio_por, sorteio_onde, premios, entidade_nome')
          .eq('rifa_id', idNum)
          .single();
        if (errRifa) throw errRifa;

        if (ativo) {
          setRifa((resRifa as RifaImpressao) ?? null);
          setTaloes((resTaloes as TalaoImpressao[]) ?? []);
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar. Tente novamente.'));
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [rifaId, idNum, navigate, reload, pronto, user]);

  const taloesVisiveis = useMemo(() => {
    if (!soComLivres) return taloes;
    return taloes.filter((t) => t.livres > 0);
  }, [taloes, soComLivres]);

  const linkDe = (token: string) => `${window.location.origin}/t/${token}`;

  if (carregando) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[880px] px-4 py-8">
          <div className="h-8 w-64 animate-pulse rounded bg-secondary-100" />
          <div className="mt-2 h-4 w-40 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-[500px] w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-lock-2-line text-2xl" />
            </span>
            <h1 className="mt-4 text-xl font-medium text-foreground-950">Acesso negado</h1>
            <p className="mt-2 text-sm text-foreground-600">{erro}</p>
            <button
              type="button"
              onClick={() => setReload((r) => r + 1)}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Tentar de novo
            </button>
            <Link
              to="/minhas-rifas"
              className="mt-3 block cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              Voltar para minhas ações entre amigos
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!rifa) return null;

  return (
    <div className="pagina-imprimir min-h-screen bg-background-100">
      {/* Barra superior — fora da impressão */}
      <div className="no-print border-b border-secondary-200 bg-white">
        <div className="mx-auto flex w-full max-w-[880px] flex-col gap-4 px-4 py-4 md:flex-row md:items-end md:justify-between">
          <div>
            <Link
              to={`/sorteio/${rifaId}/pronta`}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-800"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              Voltar
            </Link>
            <h1 className="mt-1 text-[26px] font-normal text-foreground-950">
              Talões do {rifa.titulo}
            </h1>
            <p className="mt-0.5 text-sm text-secondary-500">
              {taloesVisiveis.length}{' '}
              {taloesVisiveis.length === 1 ? 'talão pronto' : 'talões prontos'} para imprimir
            </p>
          </div>

          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-foreground-800">
              <input
                type="checkbox"
                checked={soComLivres}
                onChange={(e) => setSoComLivres(e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-primary-500"
              />
              Só os que ainda têm números livres
            </label>
            <button
              type="button"
              onClick={() => window.print()}
              disabled={taloesVisiveis.length === 0}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-300"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-printer-line" />
              </span>
              Imprimir todos
            </button>
          </div>
        </div>
      </div>

      {/* Prévia das folhas */}
      <div className="mx-auto w-full max-w-[880px] px-4 py-8">
        {taloesVisiveis.length === 0 ? (
          <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
            <div className="h-[10px] w-full bg-primary-500" />
            <div className="p-8 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-coupon-3-line text-2xl" />
              </span>
              <p className="mx-auto mt-4 max-w-sm text-[15px] text-foreground-800">
                Nenhum talão com números livres. Desmarque o filtro para ver todos.
              </p>
            </div>
          </div>
        ) : (
          taloesVisiveis.map((t) => (
            <FolhaTalao key={t.talao_id} rifa={rifa} talao={t} link={linkDe(t.token)} />
          ))
        )}
      </div>
    </div>
  );
}